import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { http, errText } from "@/lib/api";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const fmt = (s) => (s ? new Date(s).toLocaleString() : "");
const th = "text-left text-xs uppercase tracking-[0.15em] text-[#6E685F] font-semibold py-3 px-4 whitespace-nowrap";
const td = "py-4 px-4 text-sm text-[#2D2A26] align-top";

const Table = ({ testId, head, children, empty }) => (
  <div className="overflow-x-auto rounded-2xl border border-[#E6E0D4] bg-white">
    <table data-testid={testId} className="w-full min-w-[720px]">
      <thead className="bg-[#F4F0E8]"><tr>{head.map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-[#EFEAE0]">{children}</tbody>
    </table>
    {empty && <p className="p-8 text-center text-sm text-[#6E685F]">Nothing here yet.</p>}
  </div>
);

export const AdminDashboard = () => {
  const [d, setD] = useState({ bookings: [], waitlist: [], messages: [] });
  const load = async () => {
    try {
      const [b, w, m] = await Promise.all([http.get("/admin/bookings"), http.get("/admin/waitlist"), http.get("/admin/messages")]);
      setD({ bookings: b.data ?? [], waitlist: w.data ?? [], messages: m.data ?? [] });
    } catch (e) { toast.error(errText(e)); }
  };
  useEffect(() => { load(); }, []);
  const update = async (id, patch) => {
    try { await http.patch(`/admin/bookings/${id}`, patch); toast.success("Booking updated"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const setStatus = (id, status) => update(id, { status });
  const stats = [["Bookings", d.bookings.length], ["Unpaid", d.bookings.filter((b) => b.payment_status !== "paid").length], ["Waitlist", d.waitlist.length], ["Messages", d.messages.length]];
  return (
    <main className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10">
      <h1 className="font-display text-5xl font-light text-[#2D2A26]">Welcome back.</h1>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map(([k, v]) => (
          <div key={k} data-testid={`admin-stat-${k.toLowerCase()}`} className="rounded-2xl border border-[#E6E0D4] p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-[#6E685F]">{k}</p>
            <p className="font-display text-4xl text-[#2D2A26] mt-1">{v}</p>
          </div>
        ))}
      </div>
      <Tabs defaultValue="bookings" className="mt-10">
        <TabsList className="bg-[#F4F0E8] rounded-full p-1">
          {["bookings", "waitlist", "messages"].map((t) => <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="rounded-full capitalize px-5">{t}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="bookings" className="mt-6">
          <Table testId="admin-bookings-table" head={["Received", "Service", "Client", "Details", "Notes / desires", "Price / PayPal", "Status"]} empty={!d.bookings.length}>
            {d.bookings.map((b) => (
              <tr key={b.id} data-testid={`admin-booking-row-${b.id}`}>
                <td className={td}>{fmt(b.created_at)}</td>
                <td className={td}>{b.service_name}</td>
                <td className={td}>{b.name}<br /><a href={`mailto:${b.email}`} className="text-[#A8613C]">{b.email}</a></td>
                <td className={td}>
                  {b.service === "moon-ritual" ? <span>{b.ritual_type || "—"} ritual<br /><span className="text-[#6E685F]">{b.moon_cycle}</span></span> : (
                    <>
                      {b.duration ? <span>{b.duration} min · </span> : null}
                      <span className="capitalize">{b.contact_method === "phone" ? `Phone · ${b.phone}` : "Video link"}</span><br />
                      <span className="text-[#6E685F]">{b.preferred_date || "Any date"} · {b.preferred_time || "Flexible"}</span>
                    </>
                  )}
                </td>
                <td className={`${td} max-w-[240px] text-[#5E584F]`}>{b.notes || "—"}</td>
                <td className={td}>
                  <span className="block mb-2">{b.amount ? `$${b.amount}` : "—"}</span>
                  <select data-testid="admin-payment-select" value={b.payment_status === "paid" ? "paid" : "unpaid"} onChange={(e) => update(b.id, { payment_status: e.target.value })}
                    className={`rounded-full border px-3 py-1.5 text-sm ${b.payment_status === "paid" ? "border-[#B9CBB9] bg-[#EBF0EC] text-[#3E5040]" : "border-[#D9D1C2] bg-white"}`}>
                    <option value="unpaid">Unpaid</option>
                    <option value="paid">Paid</option>
                  </select>
                </td>
                <td className={td}>
                  <select data-testid="admin-status-select" value={b.status} onChange={(e) => setStatus(b.id, e.target.value)} className="rounded-full border border-[#D9D1C2] px-3 py-1.5 text-sm capitalize bg-white">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </Table>
        </TabsContent>
        <TabsContent value="waitlist" className="mt-6">
          <Table testId="admin-waitlist-table" head={["Joined", "Email", "Interest"]} empty={!d.waitlist.length}>
            {d.waitlist.map((w) => <tr key={w.id}><td className={td}>{fmt(w.created_at)}</td><td className={td}>{w.email}</td><td className={td}>{w.interest}</td></tr>)}
          </Table>
        </TabsContent>
        <TabsContent value="messages" className="mt-6">
          <Table testId="admin-messages-table" head={["Received", "From", "Message"]} empty={!d.messages.length}>
            {d.messages.map((m) => <tr key={m.id}><td className={td}>{fmt(m.created_at)}</td><td className={td}>{m.name}<br /><a href={`mailto:${m.email}`} className="text-[#A8613C]">{m.email}</a></td><td className={`${td} whitespace-pre-wrap`}>{m.message}</td></tr>)}
          </Table>
        </TabsContent>
      </Tabs>
    </main>
  );
};
